import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { XClose } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'

export type BannerVariant = 'info' | 'neutral'

/**
 * - **row** (default) — one line: icon panel, title, description, then the actions on the right
 * - **card** — stacked for a narrow column: icon and title, description, then the action below,
 *   with the dismiss control as an icon button in the top-right corner
 */
export type BannerLayout = 'row' | 'card'

/** Minimum row height from the design; grows to fit a wrapped title. The icon panel and its divider span the full row. */
const rowContainer = 'flex min-h-[54px] w-full items-center overflow-hidden rounded-md border pr-4'
const cardContainer = 'relative flex w-full flex-col gap-3 rounded-md border p-4'

type VariantStyle = {
  /** Background and border color for the row. */
  container: string
  /** Divider between the icon panel and the text. */
  divider: string
  title: string
  /** Description text and the separator rule that precedes it, which share a color in the design. */
  description: string
}

const variantStyles: Record<BannerVariant, VariantStyle> = {
  info: { container: 'border-brand-100 bg-brand-50', divider: 'border-brand-100', title: 'text-brand-800', description: 'text-text-primary' },
  neutral: {
    container: 'border-border-default bg-surface-subtle',
    divider: 'border-border-default',
    title: 'text-text-primary',
    description: 'text-text-tertiary',
  },
}

export type BannerProps = {
  /** @default 'info' */
  variant?: BannerVariant
  /** @default 'row' */
  layout?: BannerLayout
  /** In the row layout, rendered in the divided panel on the left; in the card layout, inline
   * before the title. A single logo, an icon chip, or a cluster of logos. */
  icon: ReactNode
  title: string
  /** Supporting line after the title. In the row layout it is separated from the title by a
   * vertical rule and hidden below `sm`, where the title and action alone have to carry the
   * prompt, so keep it genuinely supplementary. */
  description?: string
  /** Trailing call to action, typically a `size="xs"` tonal `Button`. */
  action?: ReactNode
  /** Renders the dismiss control: a "Dismiss" text button ahead of `action` in the row layout, an
   * icon button in the corner of the card layout. Omit for a permanent banner. */
  onDismiss?: () => void
  /** Merged onto the variant's base styles for one-off overrides. */
  className?: string
}

/**
 * Banner prompting the user to complete a setup step.
 *
 * - **info** (default) — brand-blue, for an available next step
 * - **neutral** — beige, for something set up but incomplete
 */
export function Banner({ variant = 'info', layout = 'row', icon, title, description, action, onDismiss, className }: BannerProps) {
  const style = variantStyles[variant]

  // Deliberately not a live region. This is persistent page content with its own button, not a
  // status message reacting to something the user did, so role="status" would announce the whole
  // banner over whatever the user happened to be reading whenever it appeared.
  if (layout === 'card') {
    return (
      <div data-ds-component="Banner" data-ds-variant={variant} data-ds-layout={layout} className={twMerge(cardContainer, style.container, className)}>
        {onDismiss && (
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            icon={<XClose />}
            aria-label="Dismiss"
            onClick={onDismiss}
            wrapperClassName="absolute right-0 top-0"
          />
        )}
        <div className="flex flex-col gap-1">
          {/* Right padding keeps a long title clear of the dismiss button in the corner. */}
          <div className={twMerge('flex items-center gap-1.5', onDismiss && 'pr-3')}>
            <span className="flex shrink-0 items-center justify-center" aria-hidden>
              {icon}
            </span>
            <p className={twMerge('min-w-0 flex-1 text-sm font-semibold leading-5 tracking-[-0.15px]', style.title)}>{title}</p>
          </div>
          {description && <p className={twMerge('text-sm leading-5', style.description)}>{description}</p>}
        </div>
        {action && <div className="flex">{action}</div>}
      </div>
    )
  }

  return (
    <div data-ds-component="Banner" data-ds-variant={variant} data-ds-layout={layout} className={twMerge(rowContainer, style.container, className)}>
      <div className={twMerge('flex h-full min-w-[54px] shrink-0 items-center justify-center border-r px-3', style.divider)} aria-hidden>
        {icon}
      </div>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 px-3 py-2">
        <p className={twMerge('min-w-0 text-sm font-semibold leading-5 tracking-[-0.15px]', style.title)}>{title}</p>
        {description && (
          <>
            <span className={twMerge('hidden h-3.5 w-px shrink-0 bg-current sm:block', style.description)} aria-hidden />
            <p className={twMerge('hidden min-w-0 text-sm leading-5 tracking-[-0.15px] sm:block', style.description)}>{description}</p>
          </>
        )}
      </div>
      {(onDismiss || action) && (
        <div className="flex shrink-0 items-center gap-2">
          {onDismiss && (
            <Button variant="plain" color="neutral" size="xs" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  )
}
